import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import type { SkipDraftContentQuery as SkipDraftContentQueryType } from './__types__/SkipDraftContentQuery';
import { SkipDraftContentQuery } from './SkipDraftContentQuery.graphql';

type UseSkipDraftSyncProps = {
	contentId: string;
};
export const useSkipDraftSync = ({ contentId }: UseSkipDraftSyncProps) => {
	const originalStepVersion = useRef<undefined | number>(undefined);
	const originalTitle = useRef<undefined | string>(undefined);

	const { data } = useQuery<SkipDraftContentQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SkipDraftContentQuery,
		{
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		},
	);

	useEffect(() => {
		const stepVersion = data?.singleContent?.version?.ncsStepVersion;
		originalStepVersion.current = stepVersion ? parseInt(stepVersion) : undefined;
		originalTitle.current = data?.singleContent?.title ?? undefined;
	}, [data]);

	/**
	 * Returns true if either the latest NCS step version or title differ from
	 * the version/title of the published content.
	 * @param currentStepVersion the step version from the resolved editor state
	 * @param currentTitle the title from the resolved editor state
	 */
	const isEditorDirty = useCallback(
		(currentStepVersion: number, currentTitle: string | null): boolean => {
			if (!originalStepVersion.current || !originalTitle.current) {
				return true;
			}

			const stepVersionDiffers = currentStepVersion !== originalStepVersion.current;
			const titleDiffers = currentTitle !== originalTitle.current;

			return stepVersionDiffers || titleDiffers;
		},
		[],
	);

	return { isEditorDirty };
};
